import {
  createContext,
  createElement,
  forwardRef,
  ReactNode,
  useContext,
  useMemo,
  useRef,
} from 'react';
import { TileProps, TilesContextType } from '.';
import { setAlpha } from '../../design-tokens/utils';
import { createUseHtmlId } from '../../hooks/useHtmlId';
import { createUseTextLocalizer } from '../../locale/createUseTextLocalizer';
import { Localizer } from '../../locale/useTextLocalizer';
import { renderInlineCssVars, useDesignTokens } from '../../theme';
import { setRef } from '../../util/ref-util';
import { postfixTestId } from '../../util/testId';
import { Checkbox } from '../Checkbox';
import { Input } from '../Input';
import { Flow } from '../Layouts/Flow';
import { Form } from '../Layouts/Form';
import { ObjectMarker } from '../ObjectMarker';
import { ToolbarSpacer } from '../Toolbar/__internal__';
import { withLabels } from '../withLabels';
import bundle from './NovaComponents-Tile-gui-icu.properties';
import {
  TileContainer,
  TileFooter,
  TileHeader,
  useInteractiveTile,
  useTileKeyboardTooltip,
} from './__internal__';

import cx from 'classnames';
import './Tile.css';
import cn from './Tile.css.json';

const useHtmlId = createUseHtmlId('Tile');

const useTextLocalizer = createUseTextLocalizer(bundle);

const InputWithLabels = withLabels(Input);

const TilesContext = createContext<null | TilesContextType>(null);
export const useTilesContext = () => useContext(TilesContext);
export const TilesContextProvider = (props: { children?: ReactNode; value: TilesContextType }) => {
  return <TilesContext.Provider value={props.value}>{props.children}</TilesContext.Provider>;
};

export const Tile = forwardRef<HTMLDivElement, TileProps>(function Tile(props, ref) {
  const tilesContext = useTilesContext();
  const titleId = useHtmlId();
  const dt = useDesignTokens();
  const tokens = renderInlineCssVars(
    {
      bg:
        dt.themeVariant === 'high-contrast' ? dt.c.colorUiLight : setAlpha(dt.c.colorUiLight, 0.4),
      'margin-xxs': dt.spacing.core.xxs,
    },
    'tile-object-marker'
  );

  const localize = useTextLocalizer();

  const tileRef = useRef<HTMLDivElement | null>(null);
  const bodyRef = useRef<HTMLDivElement | null>(null);
  const customFooterRef = useRef<HTMLDivElement | null>(null);
  const customContentRefs = useMemo(() => [bodyRef, customFooterRef], []);
  const setKeyboardTooltipRef = useTileKeyboardTooltip(props);

  // Tiles can have two modes: browse and interactive.
  // User can toggle tile between browse and interactive using F2.  If not interactive, the tile itself
  // can still receive keyboard focus. Default is to start in browse mode.
  const { isInteractive, ...interactiveHandlers } = useInteractiveTile({
    tileRef,
    customContentRefs,
  });

  const generateObjectMetadataTableRow = (key: string, value: string, isAddtional = false) => {
    return value ? (
      <InputWithLabels
        key={key}
        label={
          isAddtional
            ? localize('objectMetadata.addition.label', { key })
            : localize(`objectMetadata.${key}.label`)
        }
        aria-labelledby={key}
        type="text"
        editable={false}
        value={value}
        onChange={() => {}}
        fieldWidth="100%"
        tabindex={0}
        testId={postfixTestId(props.testId, `metadata-${key}`)}
        wrapLabel={false}
        labelStyle={{ fontSize: dt.fontNova.size.xs }}
        inputStyle={{ fontSize: dt.fontNova.size.xs }}
      />
    ) : null;
  };

  const generateObjectMetadataTable = () => {
    const metadataTable = [];

    const { creator, modifier, creationDate, modificationDate, location, ...addition } =
      props.metadata ?? {};

    if (creator) {
      metadataTable.push(generateObjectMetadataTableRow('creator', creator));
    }
    if (modifier) {
      metadataTable.push(generateObjectMetadataTableRow('modifier', modifier));
    }
    if (creationDate) {
      metadataTable.push(generateObjectMetadataTableRow('creationDate', creationDate));
    }
    if (modificationDate) {
      metadataTable.push(generateObjectMetadataTableRow('modificationDate', modificationDate));
    }
    if (location) {
      metadataTable.push(generateObjectMetadataTableRow('location', location));
    }

    for (const name in addition) {
      const value = addition[name];
      if (value) {
        metadataTable.push(generateObjectMetadataTableRow(name, value, true));
      }
    }

    return metadataTable.length > 0 ? (
      <Form labelPosition="left" fieldSpacing="xxs" fieldLabelWidth="20rem">
        {metadataTable}
      </Form>
    ) : null;
  };

  const metadataTable = generateObjectMetadataTable();

  const hideTileCheckbox = props.hideCheckbox ?? tilesContext?.hideCheckbox;
  const onTileSelect = props.onSelect ?? tilesContext?.onSelect;
  const tileSelected = props.selected ?? tilesContext?.isSelected(props.index);

  const tileContextRef =
    props.index !== undefined ? tilesContext?.createTileRef?.(props.index) : null;

  return (
    <TileContainer
      testId={props.testId}
      ref={(element) => {
        setRef(ref, element);
        setRef(tileRef, element);
        setRef(tileContextRef, element);
        setKeyboardTooltipRef(element);
      }}
      style={{
        ...renderInlineCssVars(
          {
            pd: dt.spacing.core.m,
          },
          'tile-content'
        ),
        ...tokens,
        ...props.style,
      }}
      className={cx(cn.tile, tileSelected && cn.selected, props.className)}
      isInteractive={isInteractive}
      ariaLabel={getTileAriaLabel({ ...props, selected: tileSelected }, localize)}
      {...(props.disableInteractiveMode ? {} : interactiveHandlers)}
      index={props.index}
      tabIndex={props.tabIndex}
      selected={tileSelected}
      onSelect={onTileSelect}
      onOpen={props.onOpen}
      ariaLabelledBy={props.ariaLabelledBy}
      ariaDescribedBy={props.ariaDescribedBy}
      disableInteractiveMode={props.disableInteractiveMode ?? tilesContext?.disableInteractiveMode}
      disableClickDelay={props.disableClickDelay}
      onContextMenu={props.onContextMenu}
      variant={props.variant}
      focusLockProps={props.focusLockProps}
    >
      <div className={cn.bar}>
        {!hideTileCheckbox ? (
          <Checkbox
            focusable={isInteractive}
            ariaLabelledBy={
              props.checkBoxAriaLabelledBy ??
              // By default, the checkbox is aria-labelledby the title element if no aria-label is provided
              (!props.checkBoxAriaLabel && props.title ? titleId : undefined)
            }
            ariaLabel={props.checkBoxAriaLabel}
            className={cn.checkbox}
            // This overrules the hiding of the checkbox except when checked.
            style={!isInteractive ? undefined : { display: 'inline-block' }}
            checked={!!tileSelected}
            testId={postfixTestId(props.testId, 'checkbox')}
            onPress={(event) => {
              onTileSelect?.({
                index: props.index ?? undefined,
                selectionArea: 'checkbox',
                altKey: event.altKey,
                ctlKey: event.ctrlKey,
                metaKey: event.metaKey,
                shiftKey: event.shiftKey,
              });
            }}
          />
        ) : undefined}

        <ToolbarSpacer />
        {props.favorited ? (
          <ObjectMarker
            className={cn.objectMarker}
            type="Favorite"
            hideLabel
            focusable={isInteractive}
            testId={postfixTestId(props.testId, 'favorite-icon')}
          />
        ) : null}
      </div>
      <div className={cn.contentContainer}>
        {props.thumbnail ? (
          <div
            tabIndex={isInteractive && props.thumbnailAriaNotHidden ? 0 : undefined}
            data-testid={postfixTestId(props.testId, 'thumbnail')}
            className={cn.thumbnailContainer}
            aria-hidden={!props.thumbnailAriaNotHidden}
          >
            {props.thumbnail}
          </div>
        ) : null}
        {hasContent(props) ? (
          <Flow
            orientation="vertical"
            className={cn.content}
            spacing="s"
            testId={postfixTestId(props.testId, 'content-flow')}
          >
            <TileHeader
              testId={postfixTestId(props.testId, 'header')}
              isInteractive={isInteractive}
              titleId={titleId}
              onOpen={props.onOpen}
              icon={props.icon}
              title={props.title}
              subTitle={props.subTitle}
              suppressTitleAsLink={props.suppressTitleAsLink}
              objectIconAriaHidden={props.objectIconAriaHidden}
              menuButtonProps={props.menuButtonProps}
              description={props.description}
            />
            {/* Wrapping div is a hook for adding future styling if needed */}
            {props.showObjectMetadataTable || props.body ? (
              <div ref={bodyRef} data-testid={postfixTestId(props.testId, 'body-segment')}>
                {metadataTable}
                {props.body}
              </div>
            ) : null}

            <TileFooter
              // customFooterRef is used to toggle the tabIndex for the custom content, so we only
              // need to set it when footerContent is present
              ref={props.footerContent ? customFooterRef : undefined}
              testId={postfixTestId(props.testId, 'footer')}
              objectMarkers={props.objectMarkers?.map((objectMarkerProps) => ({
                ...objectMarkerProps,
                focusable: objectMarkerProps.focusable === false ? false : isInteractive,
              }))}
            >
              {props.footerContent}
            </TileFooter>
          </Flow>
        ) : null}
      </div>
    </TileContainer>
  );
});

const hasContent = (props: TileProps): boolean =>
  !!(
    props.icon ||
    props.title ||
    props.description ||
    props.menuButtonProps ||
    props.showObjectMetadataTable ||
    props.body ||
    props.footerContent ||
    (props.objectMarkers && props.objectMarkers.length > 0)
  );

const getTileAriaLabel = (props: TileProps, localize: Localizer) => {
  let tileLabel = props.ariaLabel;

  // If the consumer hasn't provided an aria label, create our own.
  if ((tileLabel === undefined || tileLabel.length === 0) && props.title) {
    if (props.favorited && props.selected) {
      tileLabel = localize('objectnameFavoritedSelected.aria', { objectName: props.title });
    } else if (props.favorited && !props.selected) {
      tileLabel = localize('objectnameFavorited.aria', { objectName: props.title });
    } else if (!props.favorited && props.selected) {
      tileLabel = localize('objectnameSelected.aria', { objectName: props.title });
    } else if (!props.favorited && !props.selected) {
      tileLabel = props.title;
    }
  }
  return tileLabel;
};
